import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { docSignAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, FileText, CheckCircle, Clock, Eye, Send, PenTool, Shield, Printer } from 'lucide-react';

const ICON_MAP = { created: FileText, sent: Send, viewed: Eye, signed: PenTool };
const COLOR_MAP = { created: 'text-blue-500', sent: 'text-orange-500', viewed: 'text-purple-500', signed: 'text-green-600' };

export default function AuditReport() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await docSignAPI.getAudit(token); setData(res.data); }
      catch { toast.error('Failed to load audit report'); }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />;
  if (!data) return <div className="text-center py-20 text-gray-400">Audit report not found</div>;

  return (
    <div data-testid="audit-report-page" className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      </div>

      {/* Header */}
      <Card className="border-gray-200 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#E31937]/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#E31937]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Completed Document Audit Report</h1>
              <p className="text-xs text-gray-500 mt-0.5">Canadian Truck Sales</p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto border-gray-300 text-xs" onClick={() => window.print()} data-testid="print-audit-btn">
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Title</p>
              <p className="font-semibold text-gray-900">{data.template_name}</p>
            </div>
            <div>
              <p className="text-gray-500">Document ID</p>
              <p className="font-mono text-xs text-gray-700">{data.id}</p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <Badge className={data.status === 'signed' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-amber-100 text-amber-800 border border-amber-300'}>
                {data.status === 'signed' ? <><CheckCircle className="w-3 h-3 mr-1" />Completed</> : <><Clock className="w-3 h-3 mr-1" />Pending</>}
              </Badge>
            </div>
            <div>
              <p className="text-gray-500">Timezone</p>
              <p className="text-gray-700">UTC</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signer Info */}
      <Card className="border-gray-200 mb-6">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-gray-900 uppercase mb-3">Signer</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><p className="text-gray-500">Name</p><p className="font-medium">{data.signer_name}</p></div>
            <div><p className="text-gray-500">Email</p><p className="font-medium">{data.signer_email}</p></div>
            {data.signed_at && <div><p className="text-gray-500">Signed At</p><p className="font-medium">{new Date(data.signed_at).toLocaleString()}</p></div>}
            {data.signature_data?.ip_address && <div><p className="text-gray-500">IP Address</p><p className="font-mono text-xs">{data.signature_data.ip_address}</p></div>}
          </div>
          {data.signature_data?.value && (
            <div className="mt-3 p-3 border-b-2 border-gray-900 bg-gray-50 text-center rounded-t">
              <p className="text-2xl text-gray-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>{data.signature_data.value}</p>
              <p className="text-[10px] text-gray-400 mt-1">Electronic Signature</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Log */}
      <Card className="border-gray-200 mb-6">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-gray-900 uppercase mb-3">Activity Log</h3>
          <div className="space-y-0">
            {(data.audit_trail || []).map((entry, i) => {
              const Icon = ICON_MAP[entry.action] || FileText;
              const colorClass = COLOR_MAP[entry.action] || 'text-gray-500';
              return (
                <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${entry.action === 'signed' ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-4 h-4 ${colorClass}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium">{entry.detail}</p>
                    <div className="flex flex-wrap gap-x-4 text-[11px] text-gray-500 mt-0.5">
                      <span>{new Date(entry.timestamp).toLocaleString()}</span>
                      {entry.ip && <span>IP: {entry.ip}</span>}
                      {entry.email && <span>{entry.email}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] capitalize ${entry.action === 'signed' ? 'border-green-300 text-green-600' : ''}`}>
                    {entry.action}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Submitted Data */}
      {data.submitted_data && Object.keys(data.submitted_data).length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-gray-900 uppercase mb-3">Submitted Field Data</h3>
            <div className="space-y-1">
              {(data.fields || []).map(f => (
                <div key={f.id} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500">{f.label}</span>
                  <span className="font-medium text-gray-900">{data.submitted_data[f.id]?.toString() || '—'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
